import { leadTimeTierRatio } from '@misumi/shared'
import { amountFromCents, centsFromAmount, formatMoney } from './cart-money'
import { formatLongWeekdayDate } from './long-weekday-date'

/**
 * UID-96 (C2) — custom-delivery region tiers for the "Ship as ready" custom
 * (made-to-order) shipment card. The build previously offered three
 * service-level options (EXPEDITED/STANDARD/OVERSEAS, regions US/North
 * America/Overseas — see `DeliveryOption`/`deriveDeliveryOptions` in
 * `cart-derivation.ts`, which remains the order-level ship-together delivery
 * choice and is untouched here). The prototype's custom-bucket region picker
 * (`_regDefs`/`custOptions`, `Fictiv Platform.dc.html` line ~7272/7330) offers
 * FOUR tiers — US / MX / IN / CN — each with its own lead time and a price
 * RATIO applied to the custom-bucket subtotal (not a flat fee).
 *
 * UID-96/C2 originally kept this FE-only (no BFF field, no DB migration). That
 * posture is SUPERSEDED by ADR-0085 / UID-278: the tier selection is now canonical,
 * persisted cart state (`Cart.customLeadTimeSelections`, migration 3014) and the
 * server is authoritative for repricing — `PUT /cart/lines/{sourceableId}/lead-time-tier`
 * re-derives `CartSummary.partsSubtotalAmount` at the chosen tier. This module stays
 * as the FE's DISPLAY source (labels / regions / lead-time days / arrival-date
 * formatting) ONLY; the `priceRatio` values are now PULLED from the single canonical
 * source `@misumi/shared` (`custom-lead-time-tiers.ts` `CUSTOM_LEAD_TIME_TIERS` /
 * `leadTimeTierRatio`) — the same table the server prices from — so the two can no
 * longer drift out of sync (code-review warning #2). Those ratios mirror the
 * prototype's own client-computed math (`_custRaw*rd.f`) exactly.
 */
export type RegionTierId = 'us' | 'mx' | 'in' | 'cn'

export interface RegionTierDef {
  readonly id: RegionTierId
  readonly label: string
  readonly region: string
  /** Lead time in days FROM TODAY (the moment the buyer is viewing the
   * cart) — confirmed against the prototype's own `_addD` (`Fictiv
   * Platform.dc.html` line ~7274-7275: `_shipBase=new
   * Date('2026-07-20T00:00')` — the prototype's fixed demo stand-in for
   * "now", since a static prototype has no live clock — and
   * `_addD=n=>new Date(_shipBase.getTime()+n*864e5)`, which `custOptions`
   * (line ~7330: `_addD(rd.days)`) calls directly on each tier's `days`
   * with NO other offset). There is no separate "custom-bucket-ready" date
   * distinct from today in the source — each tier's day count already
   * covers make + ship + transit from now, so `ShipmentBreakdown` passing
   * `baseDate={today}` (wall-clock `now`, the live equivalent of the
   * prototype's fixed `_shipBase`) into this module is correct as-is. */
  readonly days: number
  /**
   * Per-region UNIT-COST multiplier for this tier (ADR-0085 A1) — NOT a shipping premium.
   * A sub-1.0 ratio (`in` 0.86, `cn` 0.78) only makes sense as a per-manufacturing-region
   * reprice of the part itself; shipping does not go negative. PULLED from the canonical
   * `@misumi/shared` table (`leadTimeTierRatio`), the same source the server prices from.
   */
  readonly priceRatio: number
}

/**
 * The four custom-shipment region tiers. DISPLAY fields (label / region / lead-time days)
 * are pulled verbatim from the prototype's `_regDefs` (Fictiv Platform.dc.html):
 * `{ us:{label:'Expedited',region:'United States',days:22},
 *    mx:{label:'Standard',region:'North America',days:30},
 *    in:{label:'Overseas · fastest',region:'Overseas',days:40},
 *    cn:{label:'Overseas · cost-effective',region:'Overseas',days:46} }`
 * The `priceRatio` for each is looked up from the single canonical `@misumi/shared` table
 * (`leadTimeTierRatio`) — the same ratios the server reprices from — so this FE copy can no
 * longer drift (code-review warning #2). The `?? 1` fallbacks are unreachable (all four ids
 * are known) and satisfy the strict `number` type.
 */
const REGION_TIER_DISPLAY: ReadonlyArray<Omit<RegionTierDef, 'priceRatio'>> = [
  { id: 'us', label: 'Expedited', region: 'United States', days: 22 },
  { id: 'mx', label: 'Standard', region: 'North America', days: 30 },
  { id: 'in', label: 'Overseas · fastest', region: 'Overseas', days: 40 },
  { id: 'cn', label: 'Overseas · cost-effective', region: 'Overseas', days: 46 },
]

export const REGION_TIERS: readonly RegionTierDef[] = REGION_TIER_DISPLAY.map((tier) => ({
  ...tier,
  priceRatio: leadTimeTierRatio(tier.id) ?? 1,
}))

/**
 * Code-review M1 — the contract types `CustomLeadTimeSelection.leadTimeTierId`
 * as a bare `string` (deliberately: "the tier catalog itself can evolve
 * without a contract change"), so a persisted selection can in principle name
 * a tier id this FE table doesn't know about. Callers MUST run a persisted id
 * through this guard before treating it as a `RegionTierId` — passing an
 * unrecognized id straight into `deriveRegionTierOptions` used to silently
 * fall back to `us`/Expedited (via `?? 1` / `options[0]`) and render a
 * confident-looking wrong tier + price. Callers that fail this guard should
 * treat the selection as `null` (unresolved), never coerce it.
 */
export function isKnownRegionTierId(value: string): value is RegionTierId {
  return REGION_TIERS.some((tier) => tier.id === value)
}

export interface RegionTierOption extends RegionTierDef {
  /** ISO date (YYYY-MM-DD). */
  readonly arrivalDate: string
  /** Human label, e.g. "Wednesday, Aug 19" — the prototype's `_fmtDow` (long
   * weekday, 2-digit day), distinct from the cart line's own short
   * `shipDateLabel` style ("Wed, Aug 19"). */
  readonly arrivalLabel: string
  readonly priceLabel: string
  readonly selected: boolean
}

/* The region-tier date uses the prototype's `_fmtDow` formatter
 * (`weekday:'long'`, Fictiv Platform.dc.html line ~7290), NOT the short
 * `_fmtDowShort` style used for the cart line's own per-line ship date
 * (line ~7293). This value is entirely FE-computed (no BFF round trip), so
 * there's no backend constraint pulling it toward the short style. */

/** Adds whole days by raw epoch-millis offset — deliberately NOT
 * calendar-aware in the caller's local timezone. `baseDate` is the caller's
 * wall-clock `now` (`ShipmentBreakdown`'s `today = new Date()`), which
 * carries a local time-of-day; formatting the result via `getUTC*` (below)
 * means a viewer west of UTC can see this land on the calendar day that
 * follows their local "today" (e.g. 8pm PST on Aug 18 is already Aug 19Z) —
 * a possible off-by-one near local midnight. Accepted for this demo-fidelity
 * feature (matches the prototype's own single fixed `_shipBase`, which has
 * no timezone question at all); a real fix would need a deliberate
 * "business day in the buyer's timezone" policy, which no spec defines yet. */
function addDaysUtc(base: Date, days: number): Date {
  return new Date(base.getTime() + days * 86_400_000)
}

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10)
}

/** `_fmtDow`'s `day:'2-digit'` zero-pads single-digit days (e.g. "Aug 05"). */
function formatArrivalLabel(d: Date): string {
  return formatLongWeekdayDate(d, { padDay: true })
}

/**
 * The four region-tier options for the custom shipment card, each with its
 * derived arrival date (base date + tier days) and a preview price.
 *
 * UID-271/ADR-0085 changed what `customSubtotalAmount` MEANS here: the tier
 * round-trips to `PUT /cart/lines/{sourceableId}/lead-time-tier` and the
 * server re-derives the cart, so the subtotal this function is called with is
 * now the CURRENT, server-repriced amount AT `selectedTierId` — not an
 * unscaled base the way it was before that ticket (when the picker never
 * round-tripped and the subtotal was always the server's untouched default).
 * To preview every OTHER tier's price from that single repriced figure, we
 * first recover the unscaled base by dividing out the selected tier's own
 * ratio, then project each tier's price from that base. The selected tier's
 * own price is passed straight through — no rounding drift vs the
 * server-authoritative amount that's actually charged. Non-selected tiers
 * remain deliberately labelled "est." by the caller (they're a client-side
 * preview of what choosing that tier would reprice to, not yet confirmed by
 * the server).
 *
 * `selectedTierId` is `null` when the server's persisted tier id isn't one
 * this FE table recognizes (code-review M1 — see `isKnownRegionTierId`).
 * In that case NO option is marked `selected`, and every option's price is
 * an equally-uncertain "est." preview (base recovery falls back to ratio 1,
 * i.e. treats the current subtotal as the base) — deliberately so nothing
 * here claims to be the confirmed current price/tier for an id we can't
 * verify.
 */
export function deriveRegionTierOptions(
  baseDate: Date,
  customSubtotalAmount: string,
  currency: string,
  selectedTierId: RegionTierId | null,
): RegionTierOption[] {
  const currentCents = centsFromAmount(customSubtotalAmount)
  const selectedRatio =
    selectedTierId !== null
      ? (REGION_TIERS.find((tier) => tier.id === selectedTierId)?.priceRatio ?? 1)
      : 1
  const baseCents = selectedRatio !== 0 ? currentCents / selectedRatio : currentCents
  return REGION_TIERS.map((tier) => {
    const arrival = addDaysUtc(baseDate, tier.days)
    const priceCents =
      selectedTierId !== null && tier.id === selectedTierId
        ? currentCents
        : Math.round(baseCents * tier.priceRatio)
    return {
      ...tier,
      arrivalDate: isoDate(arrival),
      arrivalLabel: formatArrivalLabel(arrival),
      priceLabel: formatMoney(amountFromCents(priceCents), currency),
      selected: selectedTierId !== null && tier.id === selectedTierId,
    }
  })
}
